<script setup lang="ts">
import { ref } from 'vue';
import { useRouter } from 'vue-router';
import { AuthRouteName } from '@/js/account/app/router/route-names';
import BaseLoadState from '@/js/account/components/base/BaseLoadState.vue';
import BaseTitle from '@/js/account/components/base/BaseTitle.vue';
import ProfileAvatarSection from '@/js/account/components/profile/ProfileAvatarSection.vue';
import ProfileDetailsCard from '@/js/account/components/profile/ProfileDetailsCard.vue';
import ProfileHeader from '@/js/account/components/profile/ProfileHeader.vue';
import { signOut } from '@/js/account/entities/auth';
import { useAuth } from '@/js/account/entities/session';
import { classNames } from '@/js/lib/bem';

const cn = classNames('profile-page');

const router = useRouter();
const { user, loading, fetchMe } = useAuth();
const avatarUploading = ref(false);

async function onProfileSaved({
  emailChanged,
}: {
  emailChanged: boolean
}): Promise<void> {
  // A new address resets the verification, so the account stays locked until the
  // link from the letter is opened — sign the user out and say so instead of
  // leaving them on a page every request now answers with 403.
  if (emailChanged) {
    // The address is already saved, so the message has to show even when the
    // logout request itself fails: `signOut()` drops the local session either
    // way and the server answers every account request with 403 from now on.
    await signOut().catch(() => {});
    await router.replace({ name: AuthRouteName.EmailChanged });

    return;
  }

  await fetchMe();
}
</script>

<template>
  <div :class="cn()">
    <ProfileHeader>
      <template #left>
        <BaseTitle size="md">
          My Profile
        </BaseTitle>
      </template>
    </ProfileHeader>

    <div :class="cn('content')">
      <BaseLoadState :loading="loading && !user" :empty="!user && !loading">
        <template v-if="user">
          <ProfileAvatarSection v-model:uploading="avatarUploading" />
          <ProfileDetailsCard :user="user" @saved="onProfileSaved" />
        </template>
      </BaseLoadState>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.profile-page {
  font-family: var(--font-family-sans);
  color: var(--color-text-primary);

  &__content {
    padding: 32px;
  }

  @media (max-width: 768px) {
    &__content {
      padding: 16px;
    }
  }
}
</style>
